import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from "./components/Login.tsx";
import Prompt from "./components/Prompt.tsx";
import Results from "./components/Results.tsx";
import { AuthProvider } from './context/AuthContext.tsx';
import { AdminAuthProvider } from './context/AdminAuthContext.tsx';
import AdminLogin from './components/admin/AdminLogin.tsx';
import AdminDashboard from './components/admin/AdminDashboard.tsx';
import UsersList from './components/admin/users/UsersList.tsx';
import UserCreate from './components/admin/users/UserCreate.tsx';
import UserEdit from './components/admin/users/UserEdit.tsx';
import DatabaseStats from './components/admin/database/DatabaseStats.tsx';
import DatabaseExport from './components/admin/database/DatabaseExport.tsx';
import RequireAdminAuth from './components/admin/RequireAdminAuth.tsx';
import './fonts.css';

const App: React.FC = () => {
  // Set up axios interceptors for debugging
  useEffect(() => {
    // Add request interceptor
    axios.interceptors.request.use(config => {
      console.log('🚀 Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        headers: config.headers,
        data: config.data,
        params: config.params
      });
      return config;
    }, error => {
      console.error('❌ Request Error:', error);
      return Promise.reject(error);
    });

    // Add response interceptor
    axios.interceptors.response.use(response => {
      console.log('✅ Response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers
      });
      return response;
    }, error => {
      console.error('❌ Response Error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      return Promise.reject(error);
    });
  }, []);
  
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <Router>
          <Routes>
            {/* Regular user routes */}
            <Route path="/" element={<Login />} />
            <Route path="/prompt" element={<Prompt />} />
            <Route path="/results" element={<Results />} />
            
            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <RequireAdminAuth>
                <AdminDashboard />
              </RequireAdminAuth>
            } />
            <Route path="/admin/users" element={
              <RequireAdminAuth>
                <UsersList />
              </RequireAdminAuth>
            } />
            <Route path="/admin/users/create" element={
              <RequireAdminAuth>
                <UserCreate />
              </RequireAdminAuth>
            } />
            <Route path="/admin/users/:userId" element={
              <RequireAdminAuth>
                <UserEdit />
              </RequireAdminAuth>
            } />
            <Route path="/admin/database/stats" element={
              <RequireAdminAuth>
                <DatabaseStats />
              </RequireAdminAuth>
            } />
            <Route path="/admin/database/export" element={
              <RequireAdminAuth>
                <DatabaseExport />
              </RequireAdminAuth>
            } />
            {/* Redirect /admin to /admin/login if not authenticated */}
            <Route path="/admin/*" element={<Navigate to="/admin/login" />} />
          </Routes>
        </Router>
      </AdminAuthProvider>
    </AuthProvider>
  );
};

export default App;
